import React, { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { getCurrentUser } from '../store/slices/authSlice';

interface AdminRouteProps {
  children?: React.ReactNode;
}

/**
 * AdminRoute — wraps children/Outlet and enforces admin-only access.
 *
 * Three outcomes:
 *  1. Still bootstrapping auth  → show spinner (no content flash)
 *  2. Authenticated but role !== 'admin' → redirect to /dashboard
 *  3. Not authenticated → redirect to /login
 *  4. Admin → render children / Outlet
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, isLoading, user } = useAppSelector((s) => s.auth);
  const hasToken = Boolean(localStorage.getItem('accessToken'));
  const [bootstrapping, setBootstrapping] = React.useState(hasToken && !isAuthenticated);

  useEffect(() => {
    let mounted = true;

    const bootstrap = async () => {
      if (!hasToken || isAuthenticated) {
        if (mounted) setBootstrapping(false);
        return;
      }
      try {
        await dispatch(getCurrentUser()).unwrap();
      } catch {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
      } finally {
        if (mounted) setBootstrapping(false);
      }
    };

    void bootstrap();
    return () => {
      mounted = false;
    };
  }, [hasToken, isAuthenticated, dispatch]);

  // Still resolving auth — show spinner, never flash content
  if (isLoading || bootstrapping) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500" />
      </div>
    );
  }

  // Not logged in
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Logged in but not admin
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace state={{ adminDenied: true }} />;
  }

  // Admin — render content
  return <>{children ?? <Outlet />}</>;
};

export default AdminRoute;
